/**
 * Accessible "Mes analyses" surface shared by the home tools.
 */

class SavedAnalysesSurface {
    constructor(root, manager, options = {}) {
        this.root = root;
        this.manager = manager;
        this.options = options;
        this.analyses = [];
        this.receivedShares = [];
        this.folders = [];
        this.pendingFolders = [];
        this.pageSize = 50;
        this.total = 0;
        this.hasMore = false;
        this.nextOffset = 0;
        this.refreshSequence = 0;
        this.statusRevision = 0;
        this.searchTimer = null;
        this.hasVersioning = Boolean(options.features?.includes('analysis_versioning'));
        this.hasSharing = Boolean(options.features?.includes('share_projects'));
        this.selectedVersionIds = new Set();
        this.versionLabels = new Map();
        this.connected = Boolean(options.connected);
        this.bound = false;
        this.editSession = null;
        this.editSessionAnalysis = null;
        this.editSessionTimer = null;
        this.editSessionRequest = null;
        this.editSessionOperation = null;
        this.saveRequest = null;
        this.actionMenuContract = 'saved_analysis_actions_popover_v1';
        this.activeActionMenu = null;
        this.boundDocumentActionMenuClick = (event) => this.handleDocumentActionMenuClick(event);
        this.boundActionMenuEscape = (event) => this.handleActionMenuKeydown(event);
        this.boundActionMenuScroll = () => this.handleActionMenuScroll();
        this.boundActionMenuResize = () => this.positionActionMenuPortal();
    }

    mount() {
        if (!this.root || this.bound) return this;
        this.bound = true;
        this.authState = this.root.querySelector('[data-history-auth-state]');
        this.connectedState = this.root.querySelector('[data-history-connected-state]');
        this.saveForm = this.root.querySelector('[data-history-save-form]');
        this.saveModeTitle = this.root.querySelector('[data-history-save-mode-title]');
        this.nameInput = this.root.querySelector('[data-history-name]');
        this.folderInput = this.root.querySelector('[data-history-folder]');
        this.folderOptions = this.root.querySelector('[data-history-folder-options]');
        this.folderCreateButton = this.root.querySelector('[data-history-create-folder]');
        this.folderDialog = this.root.querySelector('[data-history-folder-dialog]');
        this.folderNameInput = this.root.querySelector('[data-history-folder-name]');
        this.folderDialogStatus = this.root.querySelector('[data-history-folder-status]');
        this.saveButton = this.root.querySelector('[data-history-save]');
        this.availability = this.root.querySelector('[data-history-availability]');
        this.editSessionRegion = this.root.querySelector('[data-history-edit-session]');
        this.editSessionTitle = this.root.querySelector('[data-history-edit-session-title]');
        this.editSessionStatus = this.root.querySelector('[data-history-edit-session-status]');
        this.editSessionAction = this.root.querySelector('[data-history-edit-session-action]');
        this.editSessionVersion = this.root.querySelector('[data-history-edit-session-version]');
        this.editSessionSaveAs = this.root.querySelector('[data-history-edit-session-save-as]');
        this.editSessionEmpty = this.root.querySelector('[data-history-edit-session-empty]');
        this.searchInput = this.root.querySelector('[data-history-search]');
        this.folderFilter = this.root.querySelector('[data-history-folder-filter]');
        this.originFilter = this.root.querySelector('[data-history-origin-filter]');
        this.libraryCount = this.root.querySelector('[data-history-count]');
        this.filterSummary = this.root.querySelector('[data-history-filter-summary]');
        this.status = this.root.querySelector('[data-history-status]');
        this.list = this.root.querySelector('[data-history-list]');
        this.loadMoreButton = this.root.querySelector('[data-history-load-more]');
        this.receivedDetails = this.root.querySelector('[data-history-received-details]');
        this.receivedList = this.root.querySelector('[data-history-received-list]');
        this.receivedCount = this.root.querySelector('[data-history-received-count]');
        this.shareDialog = this.root.querySelector('[data-history-share-dialog]');
        this.revokeDialog = this.root.querySelector('[data-history-revoke-dialog]');
        this.renameDialog = this.root.querySelector('[data-history-rename-dialog]');
        this.deleteDialog = this.root.querySelector('[data-history-delete-dialog]');
        this.versionsDialog = this.root.querySelector('[data-history-versions-dialog]');
        this.versionCompareButton = this.root.querySelector(
            '[data-history-version-compare-run]',
        );
        this.versionCompareResult = this.root.querySelector(
            '[data-history-version-compare-result]',
        );

        this.authState.hidden = this.connected;
        this.connectedState.hidden = !this.connected;
        if (!this.connected) return this;

        this.saveForm.addEventListener('submit', (event) => this.handleSave(event));
        this.folderCreateButton?.addEventListener(
            'click',
            () => this.prepareFolderCreation(),
        );
        this.folderDialog?.querySelector('[data-history-folder-form]')
            ?.addEventListener('submit', (event) => this.handleCreateFolder(event));
        this.searchInput.addEventListener('input', () => this.queueFilterRefresh());
        this.folderFilter.addEventListener('change', () => {
            window.clearTimeout(this.searchTimer);
            this.searchTimer = null;
            this.refresh();
        });
        this.loadMoreButton?.addEventListener('click', () => this.refresh({ append: true }));
        this.originFilter?.addEventListener('change', () => {
            window.clearTimeout(this.searchTimer);
            this.searchTimer = null;
            this.refresh();
        });
        this.list.addEventListener('click', (event) => this.handleListAction(event));
        this.list.addEventListener(
            'toggle',
            (event) => this.handleActionMenuToggle(event),
            true,
        );
        this.list.addEventListener(
            'keydown',
            (event) => this.handleActionMenuKeydown(event),
        );
        this.root.addEventListener(
            'click',
            (event) => this.closeActionMenusOutside(event),
        );
        document.addEventListener('click', this.boundDocumentActionMenuClick);
        document.addEventListener('keydown', this.boundActionMenuEscape);
        window.addEventListener('scroll', this.boundActionMenuScroll, true);
        window.addEventListener('resize', this.boundActionMenuResize);
        this.receivedList?.addEventListener(
            'click',
            (event) => this.handleReceivedShareAction(event),
        );
        this.root.querySelector('[data-history-share-form]')
            ?.addEventListener('submit', (event) => this.handleShare(event));
        this.root.querySelector('[data-history-revoke-form]')
            ?.addEventListener('submit', (event) => this.handleRevoke(event));
        this.root.querySelector('[data-history-open-revoke]')
            ?.addEventListener('click', () => this.prepareRevoke());
        this.root.querySelector('[data-history-rename-form]')
            ?.addEventListener('submit', (event) => this.handleRename(event));
        this.root.querySelector('[data-history-delete-form]')
            ?.addEventListener('submit', (event) => this.handleDelete(event));
        this.root.querySelector('[data-history-version-create]')
            ?.addEventListener('click', () => this.handleCreateVersion());
        this.editSessionAction?.addEventListener(
            'click',
            () => this.handleEditSessionAction(),
        );
        this.editSessionVersion?.addEventListener(
            'click',
            () => this.handleEditSessionVersion(),
        );
        this.editSessionSaveAs?.addEventListener(
            'click',
            () => this.openSaveAsFlow({ reason: 'explicit-save-as' }),
        );
        this.root.querySelector('[data-history-versions-list]')
            ?.addEventListener('click', (event) => this.handleVersionAction(event));
        this.root.querySelector('[data-history-versions-list]')
            ?.addEventListener('change', (event) => this.handleVersionSelection(event));
        this.versionCompareButton?.addEventListener(
            'click',
            () => this.runVersionComparison(),
        );
        this.root.querySelectorAll('[data-history-dialog-cancel]').forEach((button) => {
            button.addEventListener('click', () => button.closest('dialog')?.close());
        });
        window.addEventListener('maplytics:history-changed', (event) => {
            if (event.detail?.source !== this) this.refresh({ quiet: true });
        });
        window.addEventListener('maplytics:saved-analysis-edit-session-opened', (event) => {
            if (event.detail?.source !== this) this.clearEditSession();
        });
        window.addEventListener(
            'maplytics:analysis-workspace-changed',
            () => this.scheduleEditSessionRefresh(),
        );
        document.addEventListener('input', () => this.scheduleEditSessionRefresh());
        document.addEventListener('change', () => this.scheduleEditSessionRefresh());
        document.addEventListener('click', () => this.scheduleEditSessionRefresh(180));

        this.updateAvailability();
        this.refresh();
        return this;
    }

    scheduleEditSessionRefresh(delay = 80) {
        if (!this.editSession || this.editSessionRequest) return;
        window.clearTimeout(this.editSessionTimer);
        this.editSessionTimer = window.setTimeout(
            () => this.refreshEditSessionState(),
            delay,
        );
    }

    refreshEditSessionState() {
        if (!this.editSession || this.editSessionRequest) return false;
        const payload = this.currentPayload();
        if (!payload || payload.tool_type !== this.editSession.tool_type) return false;
        const currentFingerprint = window.MaplyticsSavedAnalysisState
            .analysisPayloadFingerprint(payload);
        this.editSession.current_fingerprint = currentFingerprint;
        this.editSession.dirty = Boolean(
            currentFingerprint
            && currentFingerprint !== this.editSession.base_fingerprint
        );
        this.renderEditSession();
        return this.editSession.dirty;
    }

    startEditSession(analysis, payload = null) {
        const currentPayload = payload || this.currentPayload();
        this.editSession = window.MaplyticsSavedAnalysisState.createEditSession({
            analysis,
            payload: currentPayload,
            ownerMode: analysis?.read_only ? 'readonly' : 'owned',
        });
        this.editSessionAnalysis = this.editSession ? { ...analysis } : null;
        this.renderEditSession();
        if (this.editSession) {
            window.dispatchEvent(new CustomEvent(
                'maplytics:saved-analysis-edit-session-opened',
                { detail: { source: this, analysisId: this.editSession.analysis_id } },
            ));
        }
        return this.editSession;
    }

    clearEditSession() {
        window.clearTimeout(this.editSessionTimer);
        this.editSessionTimer = null;
        this.editSession = null;
        this.editSessionAnalysis = null;
        this.setSaveFormMode('new');
        this.renderEditSession();
    }

    setSaveFormMode(mode = 'new', { suggestedName = '' } = {}) {
        const saveAs = mode === 'save-as';
        if (this.saveForm) this.saveForm.dataset.saveMode = saveAs ? 'save-as' : 'new';
        if (this.saveModeTitle) {
            this.saveModeTitle.textContent = saveAs ? 'Enregistrer sous' : 'Nouvelle analyse';
        }
        if (this.saveButton) {
            this.saveButton.textContent = saveAs
                ? 'Enregistrer comme nouvelle analyse'
                : 'Enregistrer une nouvelle analyse';
        }
        if (suggestedName && this.nameInput && !this.nameInput.value.trim()) {
            this.nameInput.value = suggestedName.slice(0, 200);
        }
    }

    renderEditSession() {
        if (!this.editSessionRegion) return;
        const session = this.editSession;
        this.editSessionRegion.hidden = !session;
        if (this.editSessionEmpty) this.editSessionEmpty.hidden = Boolean(session);
        if (this.editSessionSaveAs) {
            this.editSessionSaveAs.hidden = !session;
            this.editSessionSaveAs.disabled = Boolean(this.editSessionRequest || this.saveRequest);
        }
        if (this.editSessionVersion) this.editSessionVersion.hidden = true;
        if (this.editSessionAction) this.editSessionAction.hidden = true;
        if (!session) return;
        this.editSessionTitle.textContent = `Analyse ouverte · ${session.analysis_name}`;
        this.editSessionTitle.title = this.editSessionTitle.textContent;
        const readonly = session.owner_mode === 'readonly';
        if (this.editSessionRequest) {
            this.editSessionStatus.textContent = this.editSessionOperation === 'version'
                ? 'Création de la nouvelle version…'
                : 'Mise à jour de l’analyse…';
        } else if (session.dirty) {
            this.editSessionStatus.textContent = readonly
                ? 'Modifications locales · « Enregistrer sous » crée une copie indépendante.'
                : !session.snapshot_revision
                    ? 'Révision indisponible · rouvrez l’analyse avant de la mettre à jour.'
                    : this.hasVersioning
                        ? 'Modifications détectées · mettez à jour, créez une version ou enregistrez sous.'
                        : 'Modifications détectées · mettez à jour ou enregistrez sous.';
        } else {
            this.editSessionStatus.textContent = readonly
                ? 'Lecture seule · aucune modification locale.'
                : 'Analyse enregistrée à jour.';
        }
        if (!readonly && this.editSessionAction) {
            this.editSessionAction.hidden = false;
            this.editSessionAction.disabled = Boolean(
                !session.dirty
                || !session.snapshot_revision
                || this.editSessionRequest
                || this.saveRequest
            );
        }
        if (!readonly && this.hasVersioning && this.editSessionVersion) {
            this.editSessionVersion.hidden = false;
            this.editSessionVersion.disabled = Boolean(
                !session.dirty || this.editSessionRequest || this.saveRequest
            );
        }
    }

    async handleEditSessionAction() {
        if (!this.editSession || this.editSessionRequest || this.saveRequest) return false;
        this.refreshEditSessionState();
        if (!this.editSession?.dirty) return false;
        if (this.editSession.owner_mode === 'readonly') {
            return this.openSaveAsFlow({ refresh: false });
        }
        if (!this.editSession.snapshot_revision) {
            this.setStatus(
                'Révision de sauvegarde indisponible. Rouvrez l’analyse avant de la mettre à jour.',
                'warning',
            );
            return false;
        }
        const payload = this.currentPayload();
        if (!payload || payload.tool_type !== this.editSession.tool_type) return false;
        const session = this.editSession;
        const sourceAnalysis = this.editSessionAnalysis || {};
        const folder = this.folderFor(sourceAnalysis);
        const prepared = this.prepareSavePayload(payload, folder);
        return this.runEditSessionRequest('update', async () => {
            const updated = await this.manager.replaceAnalysisSnapshot(
                session.analysis_id,
                session.snapshot_revision,
                prepared,
            );
            if (!updated) throw new Error('La mise à jour n’a pas été confirmée.');
            this.markEditSessionPersisted(payload, updated);
            this.setStatus('Analyse mise à jour.', 'success');
            return updated.id || session.analysis_id;
        });
    }

    async handleEditSessionVersion() {
        if (!this.editSession || this.editSessionRequest || this.saveRequest) return false;
        this.refreshEditSessionState();
        if (!this.editSession?.dirty || this.editSession.owner_mode === 'readonly') return false;
        if (!this.hasVersioning) {
            this.setStatus('Le versionnement n’est pas disponible avec cette offre.', 'warning');
            return false;
        }
        const payload = this.currentPayload();
        if (!payload || payload.tool_type !== this.editSession.tool_type) return false;
        const session = this.editSession;
        return this.runEditSessionRequest('version', async () => {
            const versionId = await this.manager.createVersion(
                session.analysis_id,
                '',
                payload,
            );
            const updated = typeof this.manager.loadAnalysis === 'function'
                ? await this.manager.loadAnalysis(session.analysis_id)
                : null;
            this.markEditSessionPersisted(payload, updated);
            session.active_version = versionId;
            this.setStatus('Nouvelle version enregistrée.', 'success');
            return versionId;
        });
    }

    markEditSessionPersisted(payload, analysis = null) {
        if (!this.editSession) return;
        const fingerprint = window.MaplyticsSavedAnalysisState
            .analysisPayloadFingerprint(payload);
        this.editSession.base_fingerprint = fingerprint;
        this.editSession.current_fingerprint = fingerprint;
        this.editSession.dirty = false;
        if (/^[0-9a-f]{64}$/.test(String(analysis?.snapshot_revision || ''))) {
            this.editSession.snapshot_revision = String(analysis.snapshot_revision);
        }
        if (analysis) {
            this.editSessionAnalysis = {
                ...(this.editSessionAnalysis || {}),
                ...analysis,
            };
        }
    }

    async runEditSessionRequest(operation, callback) {
        this.editSessionOperation = operation;
        this.editSessionRequest = Promise.resolve().then(callback);
        this.renderEditSession();
        this.updateAvailability();
        try {
            const result = await this.editSessionRequest;
            await this.refresh({ quiet: true });
            this.emitChanged();
            return result;
        } catch (error) {
            this.setStatus(this.errorMessage(error, 'Enregistrement impossible.'), 'error');
            return false;
        } finally {
            this.editSessionRequest = null;
            this.editSessionOperation = null;
            this.renderEditSession();
            this.updateAvailability();
        }
    }

    currentPayload() {
        try {
            return this.options.getCurrentAnalysis?.() || null;
        } catch (error) {
            console.error('Impossible de préparer l’analyse courante:', error);
            return null;
        }
    }

    updateAvailability() {
        if (!this.connected) return;
        const payload = this.currentPayload();
        const readOnly = this.manager.isOpenedReadOnly();
        const available = Boolean(payload);
        this.saveButton.disabled = !available || Boolean(
            this.saveRequest || this.editSessionRequest,
        );
        this.availability.textContent = readOnly
            ? 'Analyse reçue en lecture seule : « Enregistrer sous » crée une copie indépendante.'
            : (
                available
                    ? 'L’analyse affichée peut être enregistrée.'
                    : 'Lancez un calcul ou importez un tableau avant d’enregistrer.'
            );
    }

    async openSaveAsFlow({ reason = 'explicit-save-as', refresh = true } = {}) {
        if (!this.connected) {
            this.authState?.querySelector('a')?.focus({ preventScroll: true });
            return false;
        }
        if (refresh) await this.refresh({ quiet: true });
        const analysisName = String(this.editSession?.analysis_name || '').trim();
        this.setSaveFormMode(this.editSession ? 'save-as' : 'new', {
            suggestedName: this.editSession ? `${analysisName} · copie` : '',
        });
        this.updateAvailability();
        if (reason === 'versioning-unavailable') {
            this.setStatus(
                'Le versionnement n’est pas disponible avec cette offre. Enregistrez une nouvelle analyse indépendante pour conserver vos modifications.',
                'warning',
            );
        } else if (reason === 'explicit-save-as' && this.editSession) {
            this.setStatus(
                '« Enregistrer sous » crée une nouvelle analyse indépendante ; l’analyse ouverte reste inchangée.',
            );
        }
        this.saveForm?.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
        window.requestAnimationFrame(() => {
            this.nameInput?.focus({ preventScroll: true });
        });
        return true;
    }

    async openSaveFlow() {
        if (!this.connected) {
            this.authState?.querySelector('a')?.focus({ preventScroll: true });
            return false;
        }
        await this.refresh({ quiet: true });
        const payload = this.currentPayload();
        this.updateAvailability();
        if (
            this.editSession
            && payload
            && payload.tool_type === this.editSession.tool_type
        ) {
            this.refreshEditSessionState();
            if (!this.editSession.dirty) {
                const readOnly = this.editSession.owner_mode === 'readonly';
                this.setStatus(
                    readOnly
                        ? 'Analyse à jour · cette analyse reste ouverte en lecture seule.'
                        : 'Analyse à jour · aucune nouvelle version n’est nécessaire.',
                    'success',
                );
                this.editSessionRegion?.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
                return true;
            }
            if (this.editSession.owner_mode === 'readonly') {
                return this.openSaveAsFlow({ refresh: false });
            }
            return this.handleEditSessionAction();
        }
        return this.openSaveAsFlow({ reason: 'new-analysis', refresh: false });
    }

    async openFolderCreationFlow() {
        if (!this.connected) {
            this.authState?.querySelector('a')?.focus({ preventScroll: true });
            return false;
        }
        await this.refresh({ quiet: true });
        if (!this.connected) {
            this.authState?.querySelector('a')?.focus({ preventScroll: true });
            return false;
        }
        this.prepareFolderCreation();
        return true;
    }

    policyLabel() {
        const policy = this.manager.policy;
        if (!policy) return 'Retrouvez, classez et rouvrez vos analyses.';
        if (policy.unlimited) {
            return 'Historique sans limite de durée pour votre offre.';
        }
        const days = policy.retention_days;
        if (policy.limit != null && days != null) {
            return `${policy.limit} analyses enregistrées pendant ${days} jours avec votre offre actuelle.`;
        }
        if (days != null) {
            return `Analyses sans plafond, conservées pendant ${days} jours avec votre offre actuelle.`;
        }
        return 'Retrouvez, classez et rouvrez vos analyses.';
    }

    setStatus(message = '', state = '') {
        this.statusRevision += 1;
        this.status.textContent = message;
        if (state) this.status.dataset.state = state;
        else delete this.status.dataset.state;
        delete this.status.dataset.openIntentSequence;
    }

    beginSurfaceOpenIntent({ analysisId, source, message }) {
        let previousStatus = null;
        let intent = null;
        intent = this.manager.beginOpenIntent({
            analysisId,
            source,
            onSuperseded: () => {
                if (
                    !intent
                    || !previousStatus
                    || this.status?.dataset?.openIntentSequence !== String(intent.sequence)
                ) {
                    return;
                }
                this.setStatus(previousStatus.text, previousStatus.state);
            },
        });
        // beginOpenIntent synchronously cleans up the previous intent. Capture
        // the stable status only after that cleanup so A -> B -> C cannot make
        // B restore A's obsolete loading label when C supersedes it.
        previousStatus = {
            text: this.status?.textContent || '',
            state: this.status?.dataset?.state || '',
        };
        this.setStatus(message);
        if (this.status) {
            this.status.dataset.openIntentSequence = String(intent.sequence);
        }
        return intent;
    }

    errorMessage(error, fallback) {
        if (error?.status === 403 && error?.payload?.limit_reached) {
            return error.payload.error || 'Limite d’analyses atteinte.';
        }
        return error?.message || fallback;
    }

    queueFilterRefresh() {
        window.clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(() => {
            this.searchTimer = null;
            this.refresh();
        }, 250);
    }

    currentFilters() {
        return {
            query: this.searchInput.value.trim(),
            folder: this.folderFilter.value,
            ...(this.originFilter?.value ? { business_origin: this.originFilter.value } : {}),
        };
    }

    renderLibrarySummary() {
        if (this.libraryCount) {
            this.libraryCount.textContent = String(this.total);
            this.libraryCount.setAttribute(
                'aria-label',
                `${this.total} analyse${this.total > 1 ? 's' : ''} disponible${this.total > 1 ? 's' : ''}`,
            );
        }
        if (!this.filterSummary) return;
        const filters = this.currentFilters();
        const labels = [];
        if (filters.query) labels.push(`Recherche : « ${filters.query} »`);
        if (filters.folder) labels.push(`Dossier : ${filters.folder}`);
        if (filters.business_origin) labels.push(`Origine : ${this.originFilter.selectedOptions[0].textContent}`);
        this.filterSummary.textContent = labels.length
            ? `Filtres actifs · ${labels.join(' · ')}`
            : 'Aucun filtre actif.';
        this.filterSummary.dataset.active = String(Boolean(labels.length));
    }

    async refresh({ quiet = false, append = false, openIntent = null } = {}) {
        if (!this.connected) return;
        const sequence = ++this.refreshSequence;
        const refreshIsCurrent = () => (
            sequence === this.refreshSequence
            && (!openIntent || this.manager.isOpenIntentCurrent(openIntent))
        );
        const filters = this.currentFilters();
        const offset = append ? this.nextOffset : 0;
        if (!quiet) this.setStatus('Chargement de vos analyses…');
        this.root.dataset.libraryState = 'loading';
        const refreshStatusRevision = this.statusRevision;
        this.list.setAttribute('aria-busy', 'true');
        if (this.loadMoreButton) this.loadMoreButton.disabled = true;
        try {
            const page = await this.manager.listAnalyses(
                this.pageSize,
                offset,
                filters,
            );
            if (!refreshIsCurrent()) return;
            const incoming = Array.isArray(page.analyses) ? page.analyses : [];
            if (append) {
                const byId = new Map(this.analyses.map((analysis) => [Number(analysis.id), analysis]));
                incoming.forEach((analysis) => byId.set(Number(analysis.id), analysis));
                this.analyses = [...byId.values()];
            } else {
                this.analyses = incoming;
            }
            this.total = Number(page.total ?? this.analyses.length);
            this.hasMore = Boolean(page.has_more);
            this.nextOffset = page.next_offset ?? this.analyses.length;
            this.folders = Array.isArray(page.folders) ? page.folders : [];
            this.root.querySelector('[data-history-policy]').textContent = this.policyLabel();
            this.refreshFolders();
            if (filters.folder && !this.folderFilter.value) {
                await this.refresh({ quiet, openIntent });
                return;
            }
            this.render();
            this.renderLibrarySummary();
            this.root.dataset.libraryState = this.total ? 'ready' : 'empty';
            this.updateLoadMore();
            await this.refreshShares();
            if (!refreshIsCurrent()) return;
            if (!quiet && this.statusRevision === refreshStatusRevision) {
                const suffix = this.total > 1 ? 's' : '';
                const loaded = this.analyses.length;
                const loadedLabel = loaded < this.total ? ` ${loaded} affichées.` : '';
                this.setStatus(`${this.total} analyse${suffix} disponible${suffix}.${loadedLabel}`);
            }
        } catch (error) {
            if (error?.status === 401) {
                this.connected = false;
                this.authState.hidden = false;
                this.connectedState.hidden = true;
                return;
            }
            if (
                !refreshIsCurrent()
                || this.statusRevision !== refreshStatusRevision
            ) {
                return;
            }
            this.setStatus(this.errorMessage(error, 'Vos analyses sont indisponibles.'), 'error');
            this.renderEmpty('Impossible de charger vos analyses pour le moment.');
            this.root.dataset.libraryState = 'error';
            this.renderLibrarySummary();
        } finally {
            if (sequence === this.refreshSequence) {
                this.list.setAttribute('aria-busy', 'false');
                if (this.loadMoreButton) this.loadMoreButton.disabled = false;
            }
        }
    }

    folderFor(analysis) {
        const tag = (analysis.tags || []).find(
            (value) => typeof value === 'string' && value.startsWith('folder:'),
        );
        return tag ? tag.slice('folder:'.length) : '';
    }

    analysisTypeLabel(toolType) {
        const labels = {
            'import-table': 'Tableau importé',
            comparison: 'Comparaison archivée',
            'best-location': 'Meilleur emplacement',
            'geo-manager-competitors': 'Concurrence Geo Manager',
            'network-points': 'Points réseau',
            isochrone: 'Zone de chalandise',
        };
        return labels[toolType] || 'Autre analyse';
    }

    normalizeFolderName(value) {
        return String(value || '').trim().replace(/\s+/g, ' ');
    }

    matchingFolder(value, folders) {
        const normalized = this.normalizeFolderName(value);
        if (!normalized) return '';
        return (folders || []).find(
            (folder) => folder.localeCompare(
                normalized,
                'fr',
                { sensitivity: 'accent' },
            ) === 0,
        ) || '';
    }

    uniqueFolders(values) {
        const folders = [];
        (values || []).forEach((value) => {
            const folder = this.normalizeFolderName(value);
            if (!folder || this.matchingFolder(folder, folders)) return;
            folders.push(folder);
        });
        return folders.sort((left, right) => left.localeCompare(right, 'fr'));
    }

    selectableFolders() {
        return this.uniqueFolders([...this.folders, ...this.pendingFolders]);
    }

    refreshFolders({ selectedFolder = null } = {}) {
        const currentValue = this.folderFilter.value;
        const currentSaveFolder = selectedFolder === null
            ? this.folderInput?.value
            : selectedFolder;
        const persistedFolders = this.uniqueFolders(this.folders);
        const selectableFolders = this.selectableFolders();
        this.folderFilter.replaceChildren();
        this.folderFilter.appendChild(new Option('Tous les dossiers', ''));
        persistedFolders.forEach((folder) => {
            this.folderFilter.appendChild(new Option(folder, folder));
        });
        const persistedSelection = this.matchingFolder(currentValue, persistedFolders);
        this.folderFilter.value = persistedSelection || '';
        if (this.folderInput) {
            this.folderInput.replaceChildren();
            this.folderInput.appendChild(new Option('Sans dossier', ''));
            selectableFolders.forEach((folder) => {
                this.folderInput.appendChild(new Option(folder, folder));
            });
            const saveSelection = this.matchingFolder(
                currentSaveFolder,
                selectableFolders,
            );
            this.folderInput.value = saveSelection || '';
        }
        if (this.folderOptions) {
            this.folderOptions.replaceChildren();
            selectableFolders.forEach((folder) => {
                this.folderOptions.appendChild(new Option(folder, folder));
            });
        }
    }

    prepareFolderCreation() {
        if (!this.folderDialog || !this.folderNameInput) return;
        this.folderNameInput.value = '';
        this.folderDialogStatus.textContent = '';
        delete this.folderDialogStatus.dataset.state;
        this.openDialog(this.folderDialog);
        this.folderNameInput.focus();
    }

    handleCreateFolder(event) {
        event.preventDefault();
        const folder = this.normalizeFolderName(this.folderNameInput?.value);
        if (!folder) {
            this.folderDialogStatus.textContent = 'Saisissez un nom de dossier.';
            this.folderDialogStatus.dataset.state = 'error';
            this.folderNameInput?.focus();
            return;
        }
        if (folder.length > 80) {
            this.folderDialogStatus.textContent = 'Le nom est limité à 80 caractères.';
            this.folderDialogStatus.dataset.state = 'error';
            this.folderNameInput?.focus();
            return;
        }
        const existing = this.matchingFolder(folder, this.selectableFolders());
        const selectedFolder = existing || folder;
        if (!existing) this.pendingFolders.push(folder);
        this.refreshFolders({ selectedFolder });
        this.folderDialog.close();
        this.setStatus(
            existing
                ? `Le dossier « ${existing} » était déjà disponible et a été sélectionné.`
                : `Le dossier « ${folder} » est prêt pour cette analyse.`,
            'success',
        );
        this.folderInput?.focus({ preventScroll: true });
    }

    notifyFolderAssigned(folder, context = {}) {
        try {
            this.options.onFolderAssigned?.(folder || '', context);
        } catch (error) {
            console.error('Impossible de synchroniser le dossier affiché:', error);
        }
    }

    prepareSavePayload(payload, folder) {
        if (typeof this.options.prepareSavePayload !== 'function') return payload;
        const prepared = this.options.prepareSavePayload(payload, {
            folder: folder || '',
            source: 'save',
        });
        if (!prepared || typeof prepared !== 'object' || Array.isArray(prepared)) {
            throw new Error('La préparation du dossier a produit une analyse invalide.');
        }
        return prepared;
    }

    filteredAnalyses() {
        return this.analyses;
    }

    updateLoadMore() {
        if (!this.loadMoreButton) return;
        this.loadMoreButton.hidden = !this.hasMore;
        const remaining = Math.max(0, this.total - this.analyses.length);
        this.loadMoreButton.textContent = remaining
            ? `Charger plus (${remaining} restante${remaining > 1 ? 's' : ''})`
            : 'Charger plus';
    }

    async refreshShares() {
        try {
            const shares = await this.manager.listShares();
            this.receivedShares = shares.received;
            this.renderSharingOverview();
        } catch (error) {
            this.renderShareMessage(
                this.receivedList,
                this.errorMessage(error, 'Partages reçus indisponibles.'),
            );
        }
    }

    renderShareMessage(container, message) {
        if (!container) return;
        container.replaceChildren();
        const empty = document.createElement('p');
        empty.className = 'saved-analysis-share-empty';
        empty.textContent = message;
        container.appendChild(empty);
    }

    renderSharingOverview() {
        if (this.receivedCount) this.receivedCount.textContent = String(this.receivedShares.length);
        this.renderReceivedShares();
    }

    renderReceivedShares() {
        if (!this.receivedList) return;
        this.receivedList.replaceChildren();
        if (!this.receivedShares.length) {
            this.renderShareMessage(this.receivedList, 'Aucune analyse reçue.');
            return;
        }
        const fragment = document.createDocumentFragment();
        this.receivedShares.forEach((share) => {
            const archivedComparison = share.tool_type === 'comparison';
            const row = this.shareRow(
                share.analysis_name || 'Analyse partagée',
                archivedComparison
                    ? 'Comparaison archivée · lecture seule'
                    : 'Lecture seule',
            );
            row.dataset.analysisId = String(share.analysis_id);
            if (archivedComparison) {
                row.classList.add('saved-analysis-share-item--archived');
            } else {
                row.querySelector('[data-history-share-actions]').appendChild(
                    this.actionButton(
                        'Ouvrir en lecture seule',
                        'open-shared',
                        share.analysis_id,
                    ),
                );
            }
            fragment.appendChild(row);
        });
        this.receivedList.appendChild(fragment);
    }

    shareRow(titleLabel, metaLabel) {
        const row = document.createElement('article');
        row.className = 'saved-analysis-share-item';
        const copy = document.createElement('div');
        const title = document.createElement('strong');
        title.textContent = titleLabel;
        const meta = document.createElement('span');
        meta.textContent = metaLabel;
        copy.append(title, meta);
        const actions = document.createElement('div');
        actions.className = 'saved-analysis-share-item__actions';
        actions.dataset.historyShareActions = '';
        row.append(copy, actions);
        return row;
    }

    render() {
        const analyses = this.filteredAnalyses();
        this.closeActiveActionMenu({ restoreFocus: false });
        this.list.replaceChildren();
        if (!analyses.length) {
            this.renderEmpty(
                this.currentFilters().query || this.currentFilters().folder || this.currentFilters().business_origin
                    ? 'Aucune analyse ne correspond à ces filtres.'
                    : 'Aucune analyse enregistrée. Nommez votre premier résultat ci-dessus.',
            );
            return;
        }
        const folders = new Map();
        analyses.forEach((analysis) => {
            const folder = this.folderFor(analysis);
            if (!folders.has(folder)) folders.set(folder, new Map());
            const toolType = String(analysis.tool_type || '');
            const types = folders.get(folder);
            if (!types.has(toolType)) types.set(toolType, []);
            types.get(toolType).push(analysis);
        });
        const fragment = document.createDocumentFragment();
        folders.forEach((types, folder) => {
            const folderGroup = document.createElement('section');
            folderGroup.className = 'saved-analysis-folder-group';
            folderGroup.dataset.historyFolderGroup = folder || 'unfiled';
            const folderHeading = document.createElement('div');
            folderHeading.className = 'saved-analysis-folder-group__heading';
            const title = document.createElement('h5');
            title.textContent = folder || 'Sans dossier';
            title.title = title.textContent;
            const folderCount = [...types.values()].reduce(
                (total, items) => total + items.length,
                0,
            );
            const count = document.createElement('span');
            count.textContent = `${folderCount} analyse${folderCount > 1 ? 's' : ''}`;
            folderHeading.append(title, count);
            folderGroup.appendChild(folderHeading);
            types.forEach((items, toolType) => {
                const typeGroup = document.createElement('section');
                typeGroup.className = 'saved-analysis-type-group';
                typeGroup.dataset.historyTypeGroup = toolType || 'other';
                const typeHeading = document.createElement('div');
                typeHeading.className = 'saved-analysis-type-group__heading';
                const typeTitle = document.createElement('h6');
                typeTitle.textContent = this.analysisTypeLabel(toolType);
                const typeCount = document.createElement('span');
                typeCount.textContent = String(items.length);
                typeHeading.append(typeTitle, typeCount);
                const itemList = document.createElement('div');
                itemList.className = 'saved-analysis-type-group__items';
                items.forEach((analysis) => itemList.appendChild(this.renderAnalysis(analysis)));
                typeGroup.append(typeHeading, itemList);
                folderGroup.appendChild(typeGroup);
            });
            fragment.appendChild(folderGroup);
        });
        this.list.appendChild(fragment);
    }

    renderEmpty(message) {
        this.list.replaceChildren();
        const empty = document.createElement('p');
        empty.className = 'saved-analyses-empty';
        empty.textContent = message;
        this.list.appendChild(empty);
    }

    actionButton(label, action, analysisId, { danger = false } = {}) {
        const button = document.createElement('button');
        const icons = {
            open: '↗',
            'selective-open': '◫',
            share: '⇢',
            rename: '✎',
            versions: '↺',
            delete: '×',
            'open-shared': '↗',
            'revoke-share': '×',
            'restore-version': '↺',
        };
        button.type = 'button';
        button.className = `saved-analysis-action${danger ? ' saved-analysis-action--danger' : ''}`;
        const icon = document.createElement('span');
        icon.className = 'saved-analysis-action__icon';
        icon.textContent = icons[action] || '';
        icon.setAttribute('aria-hidden', 'true');
        const copy = document.createElement('span');
        copy.className = 'saved-analysis-action__label';
        copy.textContent = label;
        button.append(icon, copy);
        button.setAttribute('aria-label', label);
        button.title = label;
        button.dataset.historyAction = action;
        button.dataset.analysisId = String(analysisId);
        return button;
    }

    renderAnalysis(analysis) {
        const item = document.createElement('article');
        item.className = 'saved-analysis-item';
        item.dataset.analysisId = String(analysis.id);
        const managedByGeoManager = analysis.tool_type === 'geo-manager-competitors';
        const archivedComparison = analysis.tool_type === 'comparison';
        if (archivedComparison) item.classList.add('saved-analysis-item--archived');
        const ownership = archivedComparison
            ? 'archived'
            : analysis.read_only
                ? 'readonly'
                : (analysis.shared_by || analysis.share_id)
                    ? 'shared'
                    : 'owned';
        const ownershipLabels = {
            archived: 'Archivée',
            readonly: 'Lecture seule',
            shared: 'Partagée',
            owned: 'Possédée',
        };
        item.dataset.ownership = ownership;
        const listingSummary = analysis.listing_summary
            && typeof analysis.listing_summary === 'object'
            ? analysis.listing_summary
            : {};
        const displayName = String(
            analysis.display_name || analysis.analysis_name || 'Analyse enregistrée',
        );
        const thresholds = Array.isArray(listingSummary.thresholds)
            ? listingSummary.thresholds
            : [];
        const thresholdLabel = thresholds.map((threshold) => {
            const value = Number(threshold?.value);
            if (!Number.isFinite(value)) return '';
            const formatted = Number.isInteger(value)
                ? String(value)
                : value.toLocaleString('fr-FR', {maximumFractionDigits: 2});
            return `${formatted} ${threshold?.unit === 'km' ? 'km' : 'min'}`;
        }).filter(Boolean).join(' / ');
        const hiddenThresholdCount = listingSummary.thresholds_truncated
            ? Math.max(0, Number(listingSummary.threshold_count || 0) - thresholds.length)
            : 0;
        const scopeLabel = thresholdLabel
            ? `${thresholdLabel}${hiddenThresholdCount ? ` · +${hiddenThresholdCount} portée${hiddenThresholdCount > 1 ? 's' : ''}` : ''}`
            : '';

        const content = document.createElement('div');
        content.className = 'saved-analysis-item__summary';
        const badge = document.createElement('span');
        badge.className = `saved-analysis-item__ownership saved-analysis-item__ownership--${ownership}`;
        badge.textContent = ownershipLabels[ownership];
        const title = document.createElement('h4');
        title.className = 'saved-analysis-item__title';
        title.textContent = displayName;
        title.title = title.textContent;
        content.append(badge, title);

        const details = document.createElement('details');
        details.className = 'saved-analysis-item__details';
        const detailsSummary = document.createElement('summary');
        detailsSummary.textContent = 'Détails de l’analyse';
        detailsSummary.setAttribute(
            'aria-label',
            `Détails de ${displayName}`,
        );
        details.appendChild(detailsSummary);

        const meta = document.createElement('dl');
        meta.className = 'saved-analysis-item__meta';
        const appendMeta = (label, value, className = '') => {
            const pair = document.createElement('div');
            pair.className = 'saved-analysis-item__meta-pair';
            const term = document.createElement('dt');
            term.textContent = label;
            const description = document.createElement('dd');
            description.textContent = value;
            if (className) description.className = className;
            description.title = value;
            pair.append(term, description);
            meta.appendChild(pair);
            return description;
        };
        const typeLabel = this.analysisTypeLabel(analysis.tool_type);
        appendMeta('Type', typeLabel, 'saved-analysis-item__type');
        if (analysis.name_is_generic && analysis.analysis_name !== displayName) {
            appendMeta(
                'Nom enregistré',
                analysis.analysis_name || 'Nom générique historique',
                'saved-analysis-item__stored-name',
            );
        }
        if (listingSummary.business_origin?.label) {
            appendMeta('Origine métier', String(listingSummary.business_origin.label), 'saved-analysis-item__business-origin');
        }
        if (listingSummary.entry_origin?.label) {
            appendMeta(
                'Création',
                String(listingSummary.entry_origin.label),
                'saved-analysis-item__origin-kind',
            );
        }
        if (listingSummary.origin_label) {
            appendMeta(
                'Point de départ',
                String(listingSummary.origin_label),
                'saved-analysis-item__origin-label',
            );
        }
        if (listingSummary.city) {
            appendMeta('Ville', String(listingSummary.city), 'saved-analysis-item__city');
        }
        if (Number.isInteger(listingSummary.zone_count)) {
            appendMeta(
                'Zones',
                `${listingSummary.zone_count} zone${listingSummary.zone_count > 1 ? 's' : ''}`,
                'saved-analysis-item__zone-count',
            );
        }
        if (scopeLabel) {
            appendMeta('Portées', scopeLabel, 'saved-analysis-item__thresholds');
        }
        if (archivedComparison) {
            appendMeta(
                'État',
                'Conservée dans l’historique, sans réouverture dans Home',
                'saved-analysis-item__state',
            );
        }
        const folder = this.folderFor(analysis);
        if (folder) {
            appendMeta('Dossier', folder, 'saved-analysis-item__folder');
        }
        const parsedDate = new Date(analysis.updated_at || analysis.created_at);
        const dateLabel = Number.isNaN(parsedDate.getTime())
            ? 'Date de mise à jour indisponible'
            : `Mis à jour le ${parsedDate.toLocaleDateString('fr-FR')}`;
        const dateValue = Number.isNaN(parsedDate.getTime())
            ? 'Date indisponible'
            : parsedDate.toLocaleDateString('fr-FR');
        const date = appendMeta('Mis à jour', dateValue, 'saved-analysis-item__date');
        date.setAttribute('aria-label', dateLabel);
        date.title = dateLabel;
        details.appendChild(meta);

        const compactMeta = document.createElement('p');
        compactMeta.className = 'saved-analysis-item__compact-meta';
        const appendCompactMeta = (label, value, iconClass, className = '', tagName = 'span') => {
            const normalizedValue = String(value || '').trim();
            if (!normalizedValue) return;
            const item = document.createElement(tagName);
            item.className = `saved-analysis-item__compact-meta-item ${className}`.trim();
            const accessibleLabel = `${label} : ${normalizedValue}`;
            item.title = accessibleLabel;
            item.setAttribute('aria-label', accessibleLabel);
            item.tabIndex = 0;
            item.dataset.compactMeta = label.toLowerCase().replaceAll(' ', '-');
            const icon = document.createElement('i');
            icon.className = `fas ${iconClass}`;
            icon.setAttribute('aria-hidden', 'true');
            item.appendChild(icon);
            compactMeta.appendChild(item);
            return item;
        };
        appendCompactMeta('Type', typeLabel, 'fa-layer-group', 'saved-analysis-item__compact-type');
        appendCompactMeta('Origine métier', listingSummary.business_origin?.label,
            'fa-compass', 'saved-analysis-item__compact-origin');
        if (listingSummary.city || listingSummary.origin_label) {
            appendCompactMeta(
                'Lieu',
                listingSummary.origin_label || listingSummary.city,
                'fa-map-marker-alt',
                'saved-analysis-item__compact-location',
            );
        }
        if (Number.isInteger(listingSummary.zone_count)) {
            appendCompactMeta(
                'Zones',
                `${listingSummary.zone_count} zone${listingSummary.zone_count > 1 ? 's' : ''}`,
                'fa-bullseye',
                'saved-analysis-item__compact-zones',
            );
        }
        if (scopeLabel) {
            appendCompactMeta(
                'Portées',
                scopeLabel,
                'fa-clock',
                'saved-analysis-item__compact-thresholds',
            );
        }
        appendCompactMeta(
            'Dossier',
            folder || 'Sans dossier',
            'fa-folder',
            'saved-analysis-item__compact-folder',
        );
        const compactDate = appendCompactMeta(
            'Mis à jour',
            dateValue,
            'fa-calendar-alt',
            'saved-analysis-item__compact-date',
            'time',
        );
        if (compactDate) {
            compactDate.setAttribute(
                'datetime',
                Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString(),
            );
        }
        const actions = document.createElement('div');
        actions.className = 'saved-analysis-item__actions';
        const primaryActions = document.createElement('div');
        primaryActions.className = 'saved-analysis-item__primary-actions';
        if (!archivedComparison) {
            const openButton = this.actionButton('Ouvrir', 'open', analysis.id);
            openButton.classList.add('saved-analysis-action--primary');
            primaryActions.appendChild(openButton);
        }
        const secondaryActions = document.createElement('div');
        secondaryActions.className = 'saved-analysis-item__secondary-actions';
        if (['isochrone', 'import-table'].includes(analysis.tool_type)) {
            secondaryActions.appendChild(
                this.actionButton('Choisir le contenu', 'selective-open', analysis.id),
            );
        }
        if (this.hasSharing && !managedByGeoManager && !archivedComparison) {
            secondaryActions.appendChild(this.actionButton('Partager', 'share', analysis.id));
        }
        if (!managedByGeoManager) {
            secondaryActions.appendChild(this.actionButton('Modifier', 'rename', analysis.id));
            if (this.hasVersioning && analysis.tool_type !== 'comparison') {
                secondaryActions.appendChild(this.actionButton('Versions', 'versions', analysis.id));
            }
            secondaryActions.appendChild(
                this.actionButton('Supprimer', 'delete', analysis.id, { danger: true }),
            );
        }

        if (primaryActions.childElementCount) actions.appendChild(primaryActions);
        if (secondaryActions.childElementCount) {
            const actionMenu = document.createElement('details');
            actionMenu.className = 'saved-analysis-item__menu';
            const menuTrigger = document.createElement('summary');
            menuTrigger.className = 'saved-analysis-item__menu-trigger';
            menuTrigger.setAttribute(
                'aria-label',
                `Plus d’actions pour ${displayName}`,
            );
            menuTrigger.title = menuTrigger.getAttribute('aria-label');
            const menuIcon = document.createElement('span');
            menuIcon.textContent = '⋯';
            menuIcon.setAttribute('aria-hidden', 'true');
            menuTrigger.appendChild(menuIcon);
            actionMenu.append(menuTrigger, secondaryActions);
            actions.appendChild(actionMenu);
        }

        const footer = document.createElement('div');
        footer.className = 'saved-analysis-item__footer';
        footer.append(compactMeta, actions);
        item.append(content, footer, details);
        return item;
    }

    analysisById(value) {
        const id = Number.parseInt(value, 10);
        return this.analyses.find((analysis) => Number(analysis.id) === id) || null;
    }

    openDialog(dialog) {
        if (!dialog) return;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
    }

    async handleSave(event) {
        event.preventDefault();
        if (this.saveRequest || this.editSessionRequest) return false;
        const payload = this.currentPayload();
        if (!payload) {
            this.updateAvailability();
            this.setStatus('Aucun résultat à enregistrer.', 'error');
            return;
        }
        const name = this.nameInput.value.trim();
        if (!name) {
            this.nameInput.focus();
            return;
        }
        const folder = this.normalizeFolderName(this.folderInput.value);
        this.saveRequest = true;
        this.saveButton.disabled = true;
        this.renderEditSession();
        this.setStatus('Enregistrement en cours…');
        try {
            const preparedPayload = this.prepareSavePayload(payload, folder);
            const analysisId = await this.manager.saveAnalysisPayload({
                ...preparedPayload,
                analysis_name: name,
                folder,
            });
            this.manager.markOpenedAnalysis(analysisId, preparedPayload.tool_type);
            this.pendingFolders = this.pendingFolders.filter(
                (pending) => !this.matchingFolder(folder, [pending]),
            );
            this.notifyFolderAssigned(folder, {
                analysisId,
                payload: preparedPayload,
                source: 'save',
            });
            this.saveForm.reset();
            await this.refresh({ quiet: true });
            let savedAnalysis = this.analysisById(analysisId);
            try {
                savedAnalysis = await this.manager.loadAnalysis(analysisId) || savedAnalysis;
            } catch (_error) {
                // The save is already durable; reopening will recover its revision.
            }
            this.startEditSession(
                savedAnalysis || {
                    id: analysisId,
                    analysis_name: name,
                    tool_type: preparedPayload.tool_type,
                    read_only: false,
                },
                this.currentPayload() || preparedPayload,
            );
            this.setSaveFormMode('save-as');
            this.setStatus('Analyse enregistrée.', 'success');
            this.emitChanged();
            return analysisId;
        } catch (error) {
            this.setStatus(this.errorMessage(error, 'Enregistrement impossible.'), 'error');
            return false;
        } finally {
            this.saveRequest = null;
            this.updateAvailability();
            this.renderEditSession();
        }
    }

    async handleListAction(event) {
        const button = event.target.closest('[data-history-action]');
        if (!button) return;
        this.closeActiveActionMenu({ restoreFocus: false });
        const analysis = this.analysisById(button.dataset.analysisId);
        if (!analysis) return;
        if (button.dataset.historyAction === 'open') {
            await this.openAnalysis(analysis);
        } else if (button.dataset.historyAction === 'selective-open') {
            await this.openSelectiveRestore(analysis, button);
        } else if (button.dataset.historyAction === 'share') {
            this.prepareShare(analysis);
        } else if (button.dataset.historyAction === 'rename') {
            this.prepareRename(analysis);
        } else if (button.dataset.historyAction === 'delete') {
            this.prepareDelete(analysis);
        } else if (button.dataset.historyAction === 'versions') {
            await this.openVersions(analysis);
        }
    }

    handleActionMenuToggle(event) {
        const menu = event.target.closest?.('.saved-analysis-item__menu');
        if (!menu) return;
        if (!menu.open) {
            if (this.activeActionMenu?.menu === menu) {
                this.closeActiveActionMenu({ restoreFocus: false });
            }
            return;
        }
        this.list.querySelectorAll('.saved-analysis-item__menu[open]').forEach((candidate) => {
            if (candidate !== menu) {
                candidate.open = false;
            }
        });
        this.openActionMenuPortal(menu);
    }

    handleActionMenuKeydown(event) {
        if (event.key !== 'Escape') return;
        const menu = event.target.closest?.('.saved-analysis-item__menu[open]')
            || this.activeActionMenu?.menu;
        if (!menu) return;
        event.preventDefault();
        this.closeActiveActionMenu({ restoreFocus: true });
    }

    closeActionMenusOutside(event) {
        if (event.target.closest?.('.saved-analysis-item__menu')
            || event.target.closest?.('.saved-analysis-item__secondary-actions--portal')) return;
        this.closeActiveActionMenu({ restoreFocus: false });
        this.list.querySelectorAll('.saved-analysis-item__menu[open]').forEach((menu) => {
            menu.open = false;
        });
    }

    handleDocumentActionMenuClick(event) {
        const portal = event.target.closest?.('.saved-analysis-item__secondary-actions--portal');
        if (portal?.__savedAnalysesSurface === this) {
            const button = event.target.closest('[data-history-action]');
            if (button) this.handleListAction(event);
            return;
        }
        this.closeActionMenusOutside(event);
    }

    openActionMenuPortal(menu) {
        if (!menu?.open) return;
        if (this.activeActionMenu?.menu !== menu) {
            this.closeActiveActionMenu({ restoreFocus: false });
        }
        const panel = menu.querySelector('.saved-analysis-item__secondary-actions');
        const trigger = menu.querySelector('.saved-analysis-item__menu-trigger');
        if (!panel || !trigger) return;
        panel.__savedAnalysesSurface = this;
        panel.classList.add('saved-analysis-item__secondary-actions--portal');
        panel.dataset.popoverContract = this.actionMenuContract;
        document.body.appendChild(panel);
        menu.dataset.portalOpen = 'true';
        this.activeActionMenu = { menu, panel, trigger };
        this.positionActionMenuPortal();
    }

    positionActionMenuPortal() {
        const active = this.activeActionMenu;
        if (!active?.panel?.isConnected || !active.trigger?.isConnected) return;
        const triggerRect = active.trigger.getBoundingClientRect();
        const viewportGap = 8;
        const desiredWidth = Math.min(240, Math.max(180, window.innerWidth - viewportGap * 2));
        active.panel.style.width = `${desiredWidth}px`;
        active.panel.style.left = `${Math.max(viewportGap, Math.min(
            window.innerWidth - desiredWidth - viewportGap,
            triggerRect.right - desiredWidth,
        ))}px`;
        const below = triggerRect.bottom + 5;
        active.panel.style.top = `${below}px`;
        const panelRect = active.panel.getBoundingClientRect();
        const fitsBelow = panelRect.bottom <= window.innerHeight - viewportGap;
        const above = triggerRect.top - panelRect.height - 5;
        const requestedTop = !fitsBelow && above >= viewportGap ? above : below;
        const maximumTop = Math.max(viewportGap, window.innerHeight - panelRect.height - viewportGap);
        active.panel.style.top = `${Math.max(
            viewportGap,
            Math.min(maximumTop, requestedTop),
        )}px`;
        if (!fitsBelow && above >= viewportGap) {
            active.panel.dataset.placement = 'top';
        } else {
            active.panel.dataset.placement = 'bottom';
        }
    }

    handleActionMenuScroll() {
        const active = this.activeActionMenu;
        if (!active?.trigger?.isConnected || !active.panel?.isConnected) return;
        window.requestAnimationFrame(() => this.positionActionMenuPortal());
    }

    closeActiveActionMenu({ restoreFocus = false } = {}) {
        const active = this.activeActionMenu;
        if (!active) return;
        this.activeActionMenu = null;
        const { menu, panel, trigger } = active;
        panel.classList.remove('saved-analysis-item__secondary-actions--portal');
        panel.removeAttribute('data-placement');
        panel.removeAttribute('data-popover-contract');
        panel.removeAttribute('style');
        if (menu?.isConnected) {
            menu.appendChild(panel);
            menu.open = false;
            delete menu.dataset.portalOpen;
        } else {
            panel.remove();
        }
        if (restoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
    }

    async openAnalysis(analysis) {
        const intent = this.beginSurfaceOpenIntent({
            analysisId: analysis.id,
            source: 'saved-analysis-list',
            message: `Ouverture de « ${analysis.analysis_name} »…`,
        });
        try {
            const complete = await this.manager.loadAnalysisForOpen(analysis.id, {
                signal: intent.signal,
            });
            if (!this.manager.isOpenIntentCurrent(intent)) return null;
            if (!complete) throw new Error('Analyse introuvable.');
            return await this.applyOpenedAnalysis(complete, { intent });
        } catch (error) {
            if (this.manager.isOpenIntentCurrent(intent)) {
                this.setStatus(this.errorMessage(error, 'Ouverture impossible.'), 'error');
            }
            return null;
        }
    }

    async openSelectiveRestore(analysis, triggerButton = null) {
        const manifestApi = window.MaplyticsSavedAnalysisRestore;
        if (!manifestApi?.buildManifest || !manifestApi?.validateSelection) {
            this.setStatus('Le chargement sélectif est indisponible.', 'error');
            return null;
        }
        const intent = this.beginSurfaceOpenIntent({
            analysisId: analysis.id,
            source: 'saved-analysis-selective',
            message: `Préparation de « ${analysis.analysis_name} »…`,
        });
        try {
            const complete = await this.manager.loadAnalysisForOpen(analysis.id, {
                signal: intent.signal,
            });
            if (!this.manager.isOpenIntentCurrent(intent)) return null;
            if (!complete) throw new Error('Analyse introuvable.');
            const manifest = manifestApi.buildManifest(complete);
            const selectableDomains = manifest.domains.filter((domain) => domain.available);
            if (!selectableDomains.length) {
                throw new Error('Cette ancienne analyse ne contient aucun domaine restaurable.');
            }
            this.renderSelectiveRestoreDialog({
                analysis: complete,
                manifest,
                intent,
                triggerButton,
            });
            this.setStatus('Choisissez les contenus à charger.');
            return complete;
        } catch (error) {
            if (this.manager.isOpenIntentCurrent(intent)) {
                this.setStatus(this.errorMessage(error, 'Ouverture impossible.'), 'error');
            }
            return null;
        }
    }

    renderSelectiveRestoreDialog({ analysis, manifest, intent, triggerButton }) {
        this.root.querySelector('[data-history-selective-dialog]')?.remove();
        const dialog = document.createElement('dialog');
        dialog.className = 'saved-analysis-dialog saved-analysis-selective-dialog';
        dialog.dataset.historySelectiveDialog = '';
        dialog.setAttribute(
            'aria-labelledby',
            `saved-analysis-selective-title-${analysis.id}`,
        );

        const form = document.createElement('form');
        form.method = 'dialog';
        const title = document.createElement('h3');
        title.id = `saved-analysis-selective-title-${analysis.id}`;
        title.textContent = `Charger une partie de « ${analysis.analysis_name || 'Analyse'} »`;
        const introduction = document.createElement('p');
        introduction.textContent = [
            'Les contenus cochés remplaceront uniquement leur domaine.',
            'Les autres contenus affichés resteront inchangés.',
        ].join(' ');
        const fields = document.createElement('fieldset');
        fields.className = 'saved-analysis-selective-dialog__domains';
        const legend = document.createElement('legend');
        legend.textContent = 'Contenus disponibles';
        fields.appendChild(legend);

        manifest.domains.forEach((domain) => {
            const label = document.createElement('label');
            label.className = 'saved-analysis-selective-dialog__domain';
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.name = 'saved-analysis-domain';
            checkbox.value = domain.key;
            checkbox.disabled = !domain.available;
            checkbox.checked = domain.available && domain.key !== 'statistic';
            const copy = document.createElement('span');
            const heading = document.createElement('strong');
            heading.textContent = domain.label;
            copy.appendChild(heading);
            if (!domain.available) {
                const reason = document.createElement('small');
                reason.textContent = domain.unavailable_reason;
                copy.appendChild(reason);
            }
            label.append(checkbox, copy);
            fields.appendChild(label);
        });

        const status = document.createElement('p');
        status.className = 'saved-analysis-dialog__status';
        status.dataset.historySelectiveStatus = '';
        status.setAttribute('role', 'status');
        const actions = document.createElement('div');
        actions.className = 'saved-analysis-dialog__actions';
        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'ui-btn ui-btn-secondary';
        cancel.textContent = 'Annuler';
        const submit = document.createElement('button');
        submit.type = 'submit';
        submit.className = 'ui-btn ui-btn-primary';
        submit.textContent = 'Charger la sélection';
        actions.append(cancel, submit);
        form.append(title, introduction, fields, status, actions);
        dialog.appendChild(form);
        this.root.appendChild(dialog);

        const closeAndRestoreFocus = () => {
            if (dialog.open) dialog.close();
            dialog.remove();
            triggerButton?.focus?.({ preventScroll: true });
        };
        cancel.addEventListener('click', closeAndRestoreFocus, { once: true });
        dialog.addEventListener('cancel', (event) => {
            event.preventDefault();
            closeAndRestoreFocus();
        }, { once: true });
        form.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!this.manager.isOpenIntentCurrent(intent)) {
                status.textContent = 'Cette demande a été remplacée par une ouverture plus récente.';
                status.dataset.state = 'error';
                return;
            }
            const selectedDomainKeys = Array.from(
                fields.querySelectorAll('input:checked'),
                (input) => input.value,
            );
            const validation = window.MaplyticsSavedAnalysisRestore.validateSelection(
                manifest,
                selectedDomainKeys,
            );
            if (!validation.valid) {
                status.textContent = validation.reason;
                status.dataset.state = 'error';
                return;
            }
            submit.disabled = true;
            cancel.disabled = true;
            status.textContent = 'Chargement de la sélection…';
            delete status.dataset.state;
            try {
                const applied = await this.applyOpenedAnalysis(analysis, {
                    intent,
                    source: 'saved-analysis-selective',
                    restoreOptions: {
                        mode: 'selective',
                        manifest,
                        selectedDomainKeys: validation.selected_keys,
                    },
                });
                if (applied) closeAndRestoreFocus();
            } catch (error) {
                status.textContent = this.errorMessage(error, 'Chargement impossible.');
                status.dataset.state = 'error';
                submit.disabled = false;
                cancel.disabled = false;
            }
        });
        this.openDialog(dialog);
        fields.querySelector('input:not(:disabled)')?.focus();
    }

    async applyOpenedAnalysis(
        analysis,
        { intent = null, source = 'direct', restoreOptions = null } = {},
    ) {
        if (!analysis) throw new Error('Analyse introuvable.');
        const activeIntent = intent || this.manager.beginOpenIntent({
            analysisId: analysis.id,
            source,
        });
        return this.manager.runOpenIntent(activeIntent, async (openContext) => {
            openContext.assertCurrent();
            if (analysis.tool_type === 'comparison') {
                throw new Error(
                    'Cette comparaison archivée est conservée dans votre historique, '
                    + 'mais ne peut plus être ouverte dans Home.',
                );
            }
            await this.options.restoreAnalysis?.(analysis, openContext, restoreOptions);
            openContext.assertCurrent();
            const readOnly = Boolean(analysis.read_only);
            if (restoreOptions?.mode === 'selective') {
                // A hybrid workspace must never be attached to, or autosaved over,
                // the source analysis from which only a subset was restored.
                this.manager.clearOpenedAnalysis();
            } else {
                this.manager.markOpenedAnalysis(
                    analysis.id,
                    analysis.tool_type,
                    { readOnly },
                );
            }
            window.maplyticsClearHomeWorkspaceReset?.(analysis.tool_type);
            openContext.assertCurrent();
            if (
                analysis.tool_type === 'isochrone'
                && restoreOptions?.mode !== 'selective'
            ) {
                this.notifyFolderAssigned(this.folderFor(analysis), {
                    analysisId: analysis.id,
                    analysis,
                    source: 'open',
                });
            }
            openContext.assertCurrent();
            this.setStatus(
                restoreOptions?.mode === 'selective'
                    ? `Contenu sélectionné de « ${analysis.analysis_name || 'Analyse'} » chargé sans lier le workspace.`
                    : readOnly
                    ? `« ${analysis.analysis_name || 'Analyse'} » est ouverte en lecture seule.`
                    : `« ${analysis.analysis_name || 'Analyse'} » est ouverte.`,
                'success',
            );
            if (restoreOptions?.mode === 'selective') this.clearEditSession();
            else this.startEditSession(analysis);
            this.updateAvailability();
            return analysis;
        });
    }

    prepareShare(analysis) {
        if (!this.hasSharing || !this.shareDialog) return;
        this.shareDialog.querySelector('[data-history-share-analysis-id]').value = analysis.id;
        this.shareDialog.querySelector('[data-history-share-name]').textContent =
            analysis.analysis_name || 'Cette analyse';
        const email = this.shareDialog.querySelector('[data-history-share-email]');
        const status = this.shareDialog.querySelector('[data-history-share-status]');
        email.value = '';
        status.textContent = '';
        delete status.dataset.state;
        this.openDialog(this.shareDialog);
        email.focus();
    }

    async handleShare(event) {
        event.preventDefault();
        const analysisId = Number.parseInt(
            this.shareDialog.querySelector('[data-history-share-analysis-id]').value,
            10,
        );
        const email = this.shareDialog.querySelector('[data-history-share-email]').value.trim();
        const dialogStatus = this.shareDialog.querySelector('[data-history-share-status]');
        dialogStatus.textContent = 'Partage en cours…';
        delete dialogStatus.dataset.state;
        try {
            const message = await this.manager.shareAnalysisByEmail(analysisId, email);
            this.shareDialog.close();
            this.setStatus(message, 'success');
            this.emitChanged();
        } catch (error) {
            dialogStatus.textContent = this.errorMessage(error, 'Partage impossible.');
            dialogStatus.dataset.state = 'error';
        }
    }

    async handleReceivedShareAction(event) {
        const button = event.target.closest('[data-history-action="open-shared"]');
        if (!button) return;
        const analysisId = Number.parseInt(button.dataset.analysisId, 10);
        const intent = this.beginSurfaceOpenIntent({
            analysisId,
            source: 'received-share',
            message: 'Ouverture de l’analyse reçue…',
        });
        try {
            const analysis = await this.manager.loadAnalysisForOpen(analysisId, {
                signal: intent.signal,
            });
            if (!this.manager.isOpenIntentCurrent(intent)) return null;
            if (!analysis?.read_only) throw new Error('Accès partagé indisponible.');
            return await this.applyOpenedAnalysis(analysis, { intent });
        } catch (error) {
            if (this.manager.isOpenIntentCurrent(intent)) {
                const message = this.errorMessage(
                    error,
                    'Ouverture du partage impossible.',
                );
                await this.refreshShares();
                if (this.manager.isOpenIntentCurrent(intent)) {
                    this.setStatus(message, 'error');
                }
            }
            return null;
        }
    }

    prepareRevoke() {
        if (!this.revokeDialog) return;
        const email = this.revokeDialog.querySelector('[data-history-revoke-email]');
        const status = this.revokeDialog.querySelector('[data-history-revoke-status]');
        email.value = '';
        status.textContent = '';
        delete status.dataset.state;
        this.openDialog(this.revokeDialog);
        email.focus();
    }

    async handleRevoke(event) {
        event.preventDefault();
        const email = this.revokeDialog
            .querySelector('[data-history-revoke-email]')
            .value
            .trim();
        const dialogStatus = this.revokeDialog
            .querySelector('[data-history-revoke-status]');
        dialogStatus.textContent = 'Révocation en cours…';
        delete dialogStatus.dataset.state;
        try {
            const message = await this.manager.revokeSharesByEmail(email);
            this.revokeDialog.close();
            this.setStatus(message, 'success');
            this.emitChanged();
        } catch (error) {
            dialogStatus.textContent = this.errorMessage(error, 'Révocation impossible.');
            dialogStatus.dataset.state = 'error';
        }
    }

    prepareRename(analysis) {
        this.renameDialog.querySelector('[data-history-dialog-id]').value = analysis.id;
        this.renameDialog.querySelector('[data-history-dialog-name]').value = analysis.analysis_name || '';
        this.renameDialog.querySelector('[data-history-dialog-folder]').value = this.folderFor(analysis);
        this.openDialog(this.renameDialog);
        this.renameDialog.querySelector('[data-history-dialog-name]').focus();
    }

    async handleRename(event) {
        event.preventDefault();
        const id = this.renameDialog.querySelector('[data-history-dialog-id]').value;
        const name = this.renameDialog.querySelector('[data-history-dialog-name]').value.trim();
        const folder = this.renameDialog.querySelector('[data-history-dialog-folder]').value.trim();
        if (!name) return;
        try {
            const updated = await this.manager.updateAnalysis(
                id,
                { analysis_name: name, folder },
            );
            if (
                updated?.tool_type === 'isochrone'
                && Number(this.manager.openedAnalysisId) === Number(id)
            ) {
                this.notifyFolderAssigned(this.folderFor(updated), {
                    analysisId: Number(id),
                    analysis: updated,
                    source: 'rename',
                });
            }
            this.renameDialog.close();
            await this.refresh({ quiet: true });
            this.setStatus('Analyse modifiée.', 'success');
            this.emitChanged();
        } catch (error) {
            this.setStatus(this.errorMessage(error, 'Modification impossible.'), 'error');
        }
    }

    prepareDelete(analysis) {
        this.deleteDialog.querySelector('[data-history-delete-id]').value = analysis.id;
        this.deleteDialog.querySelector('[data-history-delete-name]').textContent =
            analysis.analysis_name || 'Cette analyse';
        this.openDialog(this.deleteDialog);
    }

    async handleDelete(event) {
        event.preventDefault();
        const id = this.deleteDialog.querySelector('[data-history-delete-id]').value;
        const deletedAnalysis = this.analysisById(id);
        try {
            await this.manager.deleteAnalysis(id);
            if (Number(this.manager.openedAnalysisId) === Number(id)) {
                this.manager.clearOpenedAnalysis();
                this.clearEditSession();
                if (deletedAnalysis?.tool_type === 'isochrone') {
                    this.notifyFolderAssigned('', {
                        analysisId: Number(id),
                        analysis: deletedAnalysis,
                        source: 'delete',
                    });
                }
            }
            this.deleteDialog.close();
            await this.refresh({ quiet: true });
            this.setStatus('Analyse supprimée.', 'success');
            this.emitChanged();
        } catch (error) {
            this.setStatus(this.errorMessage(error, 'Suppression impossible.'), 'error');
        }
    }

    async openVersions(analysis) {
        this.versionsDialog.querySelector('[data-history-versions-id]').value = analysis.id;
        this.versionsDialog.querySelector('[data-history-versions-title]').textContent =
            `Versions de « ${analysis.analysis_name} »`;
        this.selectedVersionIds.clear();
        this.versionLabels.clear();
        this.resetVersionComparisonResult();
        this.openDialog(this.versionsDialog);
        await this.refreshVersions(analysis.id);
    }

    async refreshVersions(analysisId) {
        const container = this.versionsDialog.querySelector('[data-history-versions-list]');
        container.replaceChildren();
        const loading = document.createElement('p');
        loading.textContent = 'Chargement des versions…';
        container.appendChild(loading);
        try {
            const versions = await this.manager.getVersions(analysisId);
            this.selectedVersionIds.clear();
            this.versionLabels.clear();
            this.resetVersionComparisonResult();
            container.replaceChildren();
            versions.forEach((version) => {
                const row = document.createElement('div');
                row.className = 'saved-analysis-version';
                const selector = document.createElement('label');
                selector.className = 'saved-analysis-version__select';
                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.value = version.id;
                checkbox.dataset.historyVersionSelect = '';
                const copy = document.createElement('div');
                const title = document.createElement('strong');
                const current = Number(version.id) === Number(analysisId);
                title.textContent = current ? 'Version actuelle' : `Version ${version.version}`;
                this.versionLabels.set(Number(version.id), title.textContent);
                const selectLabel = document.createElement('span');
                selectLabel.textContent = `Sélectionner ${title.textContent.toLowerCase()}`;
                selector.append(checkbox, selectLabel);
                const meta = document.createElement('p');
                const parsedDate = new Date(version.created_at);
                meta.textContent = [
                    Number.isNaN(parsedDate.getTime())
                        ? ''
                        : parsedDate.toLocaleString('fr-FR'),
                    version.notes || '',
                ].filter(Boolean).join(' · ');
                copy.append(title, meta);
                row.append(selector, copy);
                if (!current) {
                    row.appendChild(
                        this.actionButton('Restaurer', 'restore-version', version.id),
                    );
                }
                container.appendChild(row);
            });
            this.updateVersionComparisonControls();
        } catch (error) {
            container.replaceChildren();
            const message = document.createElement('p');
            message.textContent = this.errorMessage(error, 'Versions indisponibles.');
            container.appendChild(message);
        }
    }

    resetVersionComparisonResult() {
        if (!this.versionCompareResult) return;
        this.versionCompareResult.replaceChildren();
        this.versionCompareResult.hidden = true;
        delete this.versionCompareResult.dataset.state;
        this.updateVersionComparisonControls();
    }

    updateVersionComparisonControls() {
        if (!this.versionCompareButton || !this.versionsDialog) return;
        const selectedCount = this.selectedVersionIds.size;
        this.versionCompareButton.disabled = selectedCount !== 2;
        this.versionsDialog
            .querySelectorAll('[data-history-version-select]')
            .forEach((checkbox) => {
                checkbox.disabled = selectedCount >= 2 && !checkbox.checked;
            });
    }

    handleVersionSelection(event) {
        const checkbox = event.target.closest('[data-history-version-select]');
        if (!checkbox) return;
        const versionId = Number.parseInt(checkbox.value, 10);
        if (!Number.isInteger(versionId)) return;
        if (checkbox.checked) this.selectedVersionIds.add(versionId);
        else this.selectedVersionIds.delete(versionId);
        this.resetVersionComparisonResult();
    }

    versionDifferenceSummary(label, difference) {
        const added = Object.keys(difference?.added || {}).length;
        const removed = Object.keys(difference?.removed || {}).length;
        const modified = Object.keys(difference?.modified || {}).length;
        const total = added + removed + modified;
        return total
            ? `${label} : ${total} différence${total > 1 ? 's' : ''} (${added} ajout, ${removed} retrait, ${modified} modification).`
            : `${label} : aucune différence.`;
    }

    async runVersionComparison() {
        const versionIds = [...this.selectedVersionIds];
        if (versionIds.length !== 2 || !this.versionCompareResult) return;
        const analysisId = Number.parseInt(
            this.versionsDialog.querySelector('[data-history-versions-id]').value,
            10,
        );
        this.versionCompareButton.disabled = true;
        this.versionCompareResult.hidden = false;
        this.versionCompareResult.textContent = 'Comparaison en cours…';
        delete this.versionCompareResult.dataset.state;
        try {
            const differences = await this.manager.compareVersions(
                analysisId,
                versionIds[0],
                versionIds[1],
            );
            if (!differences || differences.error) {
                throw new Error(differences?.error || 'Comparaison indisponible.');
            }
            const heading = document.createElement('strong');
            heading.textContent = `${this.versionLabels.get(versionIds[0])} / ${this.versionLabels.get(versionIds[1])}`;
            const list = document.createElement('ul');
            [
                this.versionDifferenceSummary('Périmètre', differences.zones_config),
                this.versionDifferenceSummary('Indicateurs', differences.stats_config),
                differences.notes?.v1 === differences.notes?.v2
                    ? 'Notes : identiques.'
                    : 'Notes : différentes.',
            ].forEach((message) => {
                const item = document.createElement('li');
                item.textContent = message;
                list.appendChild(item);
            });
            this.versionCompareResult.replaceChildren(heading, list);
            this.versionCompareResult.dataset.state = 'success';
        } catch (error) {
            this.versionCompareResult.textContent = this.errorMessage(
                error,
                'Comparaison impossible.',
            );
            this.versionCompareResult.dataset.state = 'error';
        } finally {
            this.updateVersionComparisonControls();
        }
    }

    async handleCreateVersion() {
        const analysisId = Number.parseInt(
            this.versionsDialog.querySelector('[data-history-versions-id]').value,
            10,
        );
        const currentState = this.currentPayload();
        const target = this.analysisById(analysisId);
        if (
            !currentState
            || !target
            || currentState.tool_type !== target.tool_type
            || !this.manager.isOpenedAnalysis(analysisId, target.tool_type)
        ) {
            this.setStatus(
                'Ouvrez cette analyse dans son outil avant d’enregistrer son état actuel.',
                'error',
            );
            return;
        }
        const note = this.versionsDialog.querySelector('[data-history-version-note]').value.trim();
        try {
            await this.manager.createVersion(analysisId, note, currentState);
            this.versionsDialog.querySelector('[data-history-version-note]').value = '';
            await this.refreshVersions(analysisId);
            this.setStatus('Nouvelle version enregistrée.', 'success');
            if (Number(this.editSession?.analysis_id) === Number(analysisId)) {
                const fingerprint = window.MaplyticsSavedAnalysisState
                    .analysisPayloadFingerprint(currentState);
                this.editSession.base_fingerprint = fingerprint;
                this.editSession.current_fingerprint = fingerprint;
                this.editSession.dirty = false;
                this.renderEditSession();
            }
            this.emitChanged();
        } catch (error) {
            this.setStatus(this.errorMessage(error, 'Version impossible.'), 'error');
        }
    }

    async handleVersionAction(event) {
        const button = event.target.closest('[data-history-action="restore-version"]');
        if (!button) return;
        const analysisId = Number.parseInt(
            this.versionsDialog.querySelector('[data-history-versions-id]').value,
            10,
        );
        const versionId = Number.parseInt(button.dataset.analysisId, 10);
        const intent = this.beginSurfaceOpenIntent({
            analysisId,
            source: 'version-restore',
            message: 'Restauration de la version…',
        });
        try {
            const complete = await this.manager.queueVersionRestore(
                analysisId,
                intent,
                async (restoreContext) => {
                    restoreContext.assertCurrent();
                    await this.manager.restoreVersion(analysisId, versionId);
                    restoreContext.assertCurrent();
                    const restored = await this.manager.loadAnalysis(analysisId, {
                        signal: intent.signal,
                    });
                    restoreContext.assertCurrent();
                    return restored;
                },
            );
            if (!complete || !this.manager.isOpenIntentCurrent(intent)) return null;
            const applied = await this.applyOpenedAnalysis(complete, { intent });
            if (!applied || !this.manager.isOpenIntentCurrent(intent)) return null;
            await this.refreshVersions(analysisId);
            if (!this.manager.isOpenIntentCurrent(intent)) return null;
            await this.refresh({ quiet: true, openIntent: intent });
            if (!this.manager.isOpenIntentCurrent(intent)) return null;
            this.setStatus('Version restaurée et ouverte.', 'success');
            this.emitChanged();
            return complete;
        } catch (error) {
            if (this.manager.isOpenIntentCurrent(intent)) {
                this.setStatus(this.errorMessage(error, 'Restauration impossible.'), 'error');
            }
            return null;
        }
    }

    emitChanged() {
        window.dispatchEvent(
            new CustomEvent('maplytics:history-changed', { detail: { source: this } }),
        );
    }
}

window.SavedAnalysesSurface = SavedAnalysesSurface;
